import { env } from "../../env";
import { initKeycloak } from "../auth/keycloak";

/**
 * Server-side safety-diag report PDF (core renders it; the client just downloads/previews).
 * Mirrors the workorder-report export pattern, but adds an AbortController + timeout and
 * always revokes object URLs — the caveats flagged on the workorder export.
 */

const DEFAULT_TIMEOUT_MS = 30_000;

export interface SafetyDiagReportRequest {
  workOrderId: string;
  workOrderRef?: string;
  languageIsoCode?: string;
  /**
   * A specific diagnosis to export. Omitted, the server picks whichever was sealed last, which is
   * the right default and the only behaviour that existed before an intervention could show more
   * than one.
   */
  realizationId?: string;
  /** Appended to the filename when a specific attempt is exported (e.g. "2"). */
  attemptLabel?: string;
}

/** Build the admin PDF download path. */
export function buildSafetyDiagReportDownloadPath({
  workOrderId,
  languageIsoCode,
  realizationId,
}: Pick<
  SafetyDiagReportRequest,
  "workOrderId" | "languageIsoCode" | "realizationId"
>): string {
  const params = new URLSearchParams();
  if (languageIsoCode) {
    params.set("lang", languageIsoCode.split("_")[0].split("-")[0]);
  }
  const query = params.toString();
  const base = realizationId
    ? `admin/safety-diag/work-orders/${workOrderId}/realizations/${realizationId}/report/download`
    : `admin/safety-diag/work-orders/${workOrderId}/report/download`;
  return `${base}${query ? `?${query}` : ""}`;
}

/** Deterministic client-side filename (server Content-Disposition is not trusted). */
export function resolveSafetyDiagReportFilename(request: SafetyDiagReportRequest): string {
  const base = request.workOrderRef || request.workOrderId;
  // Suffixed when a specific attempt is exported, so two diagnoses on one intervention do not
  // download over each other.
  const attempt = request.attemptLabel ? `-${request.attemptLabel}` : "";
  return `safety-diag-${base}${attempt}.pdf`;
}

async function fetchSafetyDiagReportPdf(apiPath: string, signal: AbortSignal): Promise<ArrayBuffer> {
  const realm = localStorage.getItem("realm");
  const token = initKeycloak().token;
  const response = await fetch(`${env.GATEWAY_URL}/${realm}/${apiPath}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch safety-diag report (${response.status})`);
  }
  return response.arrayBuffer();
}

async function loadSafetyDiagReportFile(request: SafetyDiagReportRequest): Promise<File> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
  try {
    const data = await fetchSafetyDiagReportPdf(
      buildSafetyDiagReportDownloadPath(request),
      controller.signal
    );
    return new File([data], resolveSafetyDiagReportFilename(request), { type: "application/pdf" });
  } finally {
    clearTimeout(timeout);
  }
}

/** Download the safety-diag report PDF with a deterministic filename. */
export async function downloadSafetyDiagReport(request: SafetyDiagReportRequest): Promise<void> {
  const file = await loadSafetyDiagReportFile(request);
  const url = URL.createObjectURL(file);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Open the safety-diag report PDF in a new tab (native PDF viewer). */
export async function previewSafetyDiagReport(request: SafetyDiagReportRequest): Promise<void> {
  const previewWindow = window.open("about:blank", "_blank");
  if (!previewWindow) {
    throw new Error("Popup blocked");
  }
  try {
    const file = await loadSafetyDiagReportFile(request);
    const pdfUrl = URL.createObjectURL(file);
    previewWindow.location.href = pdfUrl;
    previewWindow.addEventListener("beforeunload", () => URL.revokeObjectURL(pdfUrl));
  } catch (error) {
    previewWindow.close();
    throw error;
  }
}
